import { INSTAGRAM_AUTOMATION_STAGE_STATUSES } from './instagramAutomationStageStatuses'
import { generateToggleStageDefs } from './instagramToggleUtils'
import { accountPrivacyUrl } from './instagramUrls'
import { InstagramSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { InstagramRecommendationKey, StageType } from '../../utils/enums'
import {
  getDomPromise,
  getWaitForElementToAppearPromise,
} from '../interruptiblePromises'
import { evaluateXpath } from '../interruptibleXpath'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

const saveKey = InstagramSettingKey.PRIVATE_ACCOUNT
const sectionTitle = 'Private account'
const targetValue = true

// Settings property functions
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? 'Private' : 'Public'))
const getIsFulfilledFn = (key) => getIsFulfilledFnFromKey(key, (value) => value)
const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// DOM helpers
async function waitForPrivateAccountModal(): Promise<HTMLElement> {
  return getWaitForElementToAppearPromise<HTMLElement>(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        'descendant::*[text() = "Switch to private account?"]',
      )[0],
  )
}

async function waitForPublicAccountModal(): Promise<HTMLElement> {
  return getWaitForElementToAppearPromise<HTMLElement>(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        'descendant::*[text() = "Switch to public account?"]',
      )[0],
  )
}

enum StageKey {
  GATHER_PRIVATE_ACCOUNT = 'GATHER_PRIVATE_ACCOUNT',
  UPDATE_PRIVATE_ACCOUNT = 'UPDATE_PRIVATE_ACCOUNT',
  UPDATE_PRIVATE_ACCOUNT_MODAL = 'UPDATE_PRIVATE_ACCOUNT_MODAL',
  UNDO_PRIVATE_ACCOUNT = 'UNDO_PRIVATE_ACCOUNT',
  UNDO_PRIVATE_ACCOUNT_MODAL = 'UPDATE_PRIVATE_ACCOUNT_MODAL',
}

const { gatherStageDefs, updateStageDefs, undoStageDefs } =
  generateToggleStageDefs(
    InstagramRecommendationKey.INSTAGRAM_PRIVATE_ACCOUNT,
    [sectionTitle],
    () => saveKey,
    () => targetValue,
    StageKey.GATHER_PRIVATE_ACCOUNT,
    StageKey.UPDATE_PRIVATE_ACCOUNT,
    StageKey.UNDO_PRIVATE_ACCOUNT,
    accountPrivacyUrl,
  )

updateStageDefs.push({
  type: StageType.ACTION,
  key: StageKey.UPDATE_PRIVATE_ACCOUNT_MODAL,
  status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
  url: accountPrivacyUrl,
  action: async (machineContext, userContext) => {
    const { isDry } = userContext

    if (isDry) {
      return
    }

    await waitForPrivateAccountModal()
    const confirmButton = evaluateXpath<HTMLElement>(
      document,
      'descendant::*[text() = "Switch to private"]',
    )[0]

    if (!confirmButton) {
      throw new Error('Could not find confirm button')
    }

    const waitForAnyChange = getDomPromise(document, () => true)
    const waitForWarning = getDomPromise(document, (node) =>
      Boolean(
        evaluateXpath(
          node,
          'descendant::*[contains(text(), "can\'t be private")]',
        ),
      ),
    )
    confirmButton.click()
    await Promise.race([
      waitForAnyChange,
      waitForWarning.then(() => {
        throw new Error('Unable to switch account to private')
      }),
    ])
  },
})

undoStageDefs.push({
  type: StageType.ACTION,
  key: StageKey.UNDO_PRIVATE_ACCOUNT_MODAL,
  status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
  url: accountPrivacyUrl,
  action: async (machineContext, userContext) => {
    const { isDry } = userContext

    if (isDry) {
      return
    }

    await waitForPublicAccountModal()
    const confirmButton = evaluateXpath<HTMLElement>(
      document,
      'descendant::*[text() = "Switch to public"]',
    )[0]

    if (!confirmButton) {
      throw new Error('Could not find confirm button')
    }

    const waitForAnyChange = getDomPromise(document, () => true)
    confirmButton.click()
    await waitForAnyChange
  },
})

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
